/**
 * Sauna Elite landing page (templates/product.sauna-elite.json) + homepage banner
 * Port of sauna-elite-developer-handoff/site/app.js, scoped per section:
 * - Wavelength explainer: mode buttons swap the text, number and highlighted
 *   wavelength rows. It explains wavelengths only; it doesn't simulate output.
 * - #anchor links inside the page scroll smoothly (instantly with reduced
 *   motion) and open a matching <details> FAQ.
 * - Optional phone bar hides while its target section or the footer is on screen.
 * - Homepage banner: one view event and a click event, pushed to the store's
 *   existing dataLayer (GTM) and Shopify customer events when they exist.
 * Prices are rendered by Liquid; this file never fetches or changes them.
 * Loaded by every nw-sel-* section, so it guards against running twice.
 */
(() => {
  if (window.nwSelLanding) return;
  window.nwSelLanding = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Wavelength explainer ---------- */
  const initExplainer = (root) => {
    if (root.dataset.elReady) return;
    root.dataset.elReady = 'true';

    const buttons = Array.from(root.querySelectorAll('[data-el-mode]'));
    const number = root.querySelector('[data-el-mode-number]');
    const unit = root.querySelector('[data-el-mode-unit]');
    const title = root.querySelector('[data-el-mode-title]');
    const description = root.querySelector('[data-el-mode-description]');
    const rows = Array.from(root.querySelectorAll('[data-el-wave]'));

    const select = (button) => {
      if (!button) return;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      if (number) number.textContent = button.dataset.number || '';
      if (unit) unit.textContent = button.dataset.unit || '';
      if (title) title.textContent = button.dataset.title || '';
      if (description) description.textContent = button.dataset.description || '';
      const wave = button.dataset.wave || 'both';
      rows.forEach((row) => row.classList.toggle('is-active', wave === 'both' || wave === row.dataset.elWave));
    };

    buttons.forEach((button) => button.addEventListener('click', () => select(button)));
    root.elSelectMode = select;
  };

  /* ---------- In-page anchors ---------- */
  const targetFor = (hash) => {
    if (!hash || hash.length < 2) return null;
    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (_) {
      return null;
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.nw-sel a[href^="#"], .nw-sel-buy a[href^="#"]');
    if (!link) return;
    const hash = link.getAttribute('href');
    const target = targetFor(hash);
    if (!target) return;
    event.preventDefault();
    if (target.tagName === 'DETAILS') target.open = true;
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (history.pushState) history.pushState(null, '', hash);
  });

  const openHashDetails = () => {
    const target = targetFor(window.location.hash);
    if (target && target.tagName === 'DETAILS') target.open = true;
  };

  /* ---------- Product gallery (port of #reserve gallery) ---------- */
  const initGallery = (root) => {
    const gallery = root.querySelector('[data-el-gallery]');
    if (!gallery || gallery.dataset.elReady) return;
    gallery.dataset.elReady = 'true';

    const mainImg = gallery.querySelector('[data-el-gallery-image]');
    const caption = gallery.querySelector('[data-el-gallery-caption]');
    const buttons = Array.from(gallery.querySelectorAll('[data-el-gallery-button]'));

    const select = (button) => {
      if (!button) return;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      if (mainImg && button.dataset.fullSrc) {
        mainImg.src = button.dataset.fullSrc;
      }
      if (mainImg && button.dataset.alt) {
        mainImg.alt = button.dataset.alt;
      }
      if (caption && button.dataset.caption) {
        caption.textContent = button.dataset.caption;
      }
    };

    buttons.forEach((button) => button.addEventListener('click', () => select(button)));
    gallery.elSelectPhoto = select;
  };
  /* ---------- Optional phone bar ---------- */
  const observers = new Map();

  const initMobileBar = (root) => {
    const bar = root.querySelector('[data-el-mobile-bar]');
    if (!bar || bar.dataset.elReady || !('IntersectionObserver' in window)) return;
    bar.dataset.elReady = 'true';

    const targets = [
      document.getElementById(bar.dataset.elTarget || 'reserve'),
      document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer'),
    ].filter(Boolean);
    const onScreen = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        });
        bar.classList.toggle('is-hidden', onScreen.size > 0);
      },
      { threshold: 0.12 }
    );
    targets.forEach((target) => observer.observe(target));
    observers.set(root, observer);
  };

  /* ---------- Homepage banner analytics ---------- */
  const track = (name, detail) => {
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: name }, detail));
    } catch (_) {}
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (_) {}
  };

  const initBanner = (root) => {
    const banner = root.querySelector('[data-el-banner]');
    if (!banner || banner.dataset.elReady) return;
    banner.dataset.elReady = 'true';
    const detail = { section: 'sauna_elite_homepage_banner', product_id: banner.dataset.productId || '' };

    banner.addEventListener('click', (event) => {
      const cta = event.target.closest('[data-elite-cta]');
      if (cta) track('nw_elite_banner_click', Object.assign({ cta: cta.dataset.eliteCta, href: cta.getAttribute('href') }, detail));
    });

    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        track('nw_elite_banner_view', detail);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(banner);
    observers.set(banner, observer);
  };

  /* ---------- Lifecycle ---------- */
  const init = (scope) => {
    scope.querySelectorAll('[data-el-explainer]').forEach(initExplainer);
    const sections = scope.matches && scope.matches('.nw-sel') ? [scope] : Array.from(scope.querySelectorAll('.nw-sel'));
    sections.forEach((section) => {
      initGallery(section);
      initMobileBar(section);
      initBanner(section);
    });
  };

  const teardown = (scope) => {
    observers.forEach((observer, key) => {
      if (scope.contains(key)) {
        observer.disconnect();
        observers.delete(key);
      }
    });
  };

  init(document);
  openHashDetails();
  window.addEventListener('hashchange', openHashDetails);

  // Theme editor: re-run when a section is added or its settings change, clean up when it's removed.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  // Selecting a mode block in the theme editor shows that mode.
  document.addEventListener('shopify:block:select', (event) => {
    const button = event.target.closest('[data-el-mode]');
    const root = button && button.closest('[data-el-explainer]');
    if (root && root.elSelectMode) root.elSelectMode(button);
    const photoBtn = event.target.closest('[data-el-gallery-button]');
    const galleryRoot = photoBtn && photoBtn.closest('[data-el-gallery]');
    if (galleryRoot && galleryRoot.elSelectPhoto) galleryRoot.elSelectPhoto(photoBtn);
  });
})();
